
import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import Sidebar from "./Sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import { supabase } from './supabaseClient';
import { updateBookingRequestStatus } from './bookingService';
import TeacherRankWidget from './components/gamification/TeacherRankWidget';
import { batchGetUserDetails } from './services/userBatchService';
import { toArabicNumerals, formatNumber, formatCurrency, transliterateNameSync } from './utils/arabicNumbers';
import { createMapsUrl } from './components/location/locationUtils';
import logger from './utils/logger';
// New dashboard components
import MetricCard from './components/dashboard/MetricCard';
import EarningsChart from './components/dashboard/EarningsChart';
import UpcomingSessionsList from './components/dashboard/UpcomingSessionsList';
import StudentRequestsTable from './components/dashboard/StudentRequestsTable';
import { fetchAllDashboardData } from './services/teacherDashboardService';
import { 
  FaMapMarkerAlt, 
  FaGlobe, 
  FaPaperclip, 
  FaLock, 
  FaBriefcase, 
  FaDollarSign, 
  FaUsers, 
  FaCalendarAlt, 
  FaChartBar,
  FaBolt,
  FaEye
} from 'react-icons/fa';


// OPTIMIZED: Wrap modal components with React.memo to prevent unnecessary re-renders
const StudentProfileModal = React.memo(function StudentProfileModal({ open, onClose, student }) {
  if (!open || !student) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl p-8 max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-black">✕</button>
        <div className="flex flex-col items-center">
          <img src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'} alt={student.full_name} className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 mb-4" />
          <h2 className="text-xl font-bold mb-2">{student.full_name}</h2>
          <div className="text-gray-600 mb-2">{student.school || student.university || ''}</div>
          <div className="text-gray-600 mb-2">{student.grade_level || ''}</div>
        </div>
      </div>
    </div>
  );
});

const RequestDetailsModal = React.memo(function RequestDetailsModal({ open, onClose, request }) {
  if (!open || !request) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl p-8 max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-black">✕</button>
        <h2 className="text-xl font-bold mb-4">Request Details</h2>
        <div className="mb-2"><b>Date:</b> {request.date}</div>
        <div className="mb-2"><b>Time:</b> {request.time_from} - {request.time_to}</div>
        <div className="mb-2">
          <b>Place:</b>{' '}
          {request.location_latitude && request.location_longitude ? (
            <a
              href={createMapsUrl(request.location_latitude, request.location_longitude)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {request.location_formatted_address || request.place || 'View Location'} ↗
            </a>
          ) : (
            request.place || 'To be arranged'
          )}
        </div>
        <div className="mb-2"><b>Type:</b> {request.request_type}</div>
        <div className="mb-2"><b>Number of Students:</b> {request.num_students}</div>
        <div className="mb-2"><b>Total Price:</b> {request.total_price} BD</div>
        <div className="mb-2"><b>Status:</b> {request.status}</div>
      </div>
    </div>
  );
});

function NotificationDropdown({ open, onClose, notifications, requests, lastMessages, user, isArabic }) {
  if (!open) return null;
  
  const getNotificationItems = () => {
    const items = [];
    
    // Add pending requests
    requests.forEach(req => {
      items.push({
        id: `req-${req.id}`,
        type: 'request',
        title: isArabic ? 'طلب جديد' : 'New Request',
        message: isArabic ? `طلب من ${req.student_name}` : `Request from ${req.student_name}`,
        time: formatTimestamp(req.created_at),
        data: req
      });
    });
    
    // Add recent messages from others
    Object.entries(lastMessages).forEach(([convId, message]) => {
      if (message && message.sender_id !== user?.id) {
        const now = new Date();
        const messageTime = new Date(message.created_at);
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        
        if (messageTime > oneDayAgo) {
          items.push({
            id: `msg-${convId}`,
            type: 'message',
            title: isArabic ? 'رسالة جديدة' : 'New Message',
            message: formatMessagePreview(message.content),
            time: formatTimestamp(message.created_at),
            data: { conversationId: convId, message }
          });
        }
      }
    });
    
    return items.sort((a, b) => new Date(b.time) - new Date(a.time));
  };
  
  const notificationItems = getNotificationItems();
  
  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute top-16 right-4 md:right-10 bg-white rounded-xl shadow-lg border border-gray-200 w-80 max-h-96 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">{isArabic ? 'الإشعارات' : 'Notifications'}</h3>
        </div>
        <div className="p-2">
          {notificationItems.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {isArabic ? 'لا توجد إشعارات' : 'No notifications'}
            </div>
          ) : (
            notificationItems.map(item => (
              <div key={item.id} className="p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${item.type === 'request' ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-gray-900">{item.title}</div>
                    <div className="text-xs text-gray-600 mt-1">{item.message}</div>
                    <div className="text-xs text-gray-400 mt-1">{item.time}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Helper functions for real-time messages
const formatMessagePreview = (content) => {
  if (!content) return 'No messages yet.';
  if (content.includes('📍') || content.includes('location')) return 'Location shared';
  if (content.includes('🌐') || content.includes('online')) return 'Location shared';
  if (content.includes('📎') || content.includes('attachment_url')) return 'File shared';
  if (content.length > 30) return content.substring(0, 30) + '...';
  return content;
};

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffHours = (now - messageTime) / (1000 * 60 * 60);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return messageTime.toLocaleDateString();
  };



const ProfilePlaceholder = () => (
  <svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 8-4 8-4s8 0 8 4v1H4v-1z" />
  </svg>
);

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const { isArabic, sidebarVisible: layoutSidebarVisible } = outletContext;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Use the correct role label based on language
  const roleLabel = isArabic ? 'طالب' : 'student';
  // Get teacher email from Supabase session instead of localStorage
  const [teacherEmail, setTeacherEmail] = useState('');
  const [requests, setRequests] = useState([]);
  const [actionLoading, setActionLoading] = useState(null); // request id
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [studentProfiles, setStudentProfiles] = useState({});
  const [conversations, setConversations] = useState([]);
  const [lastMessages, setLastMessages] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [profileModal, setProfileModal] = useState({ open: false, student: null });
  const [requestModal, setRequestModal] = useState({ open: false, request: null });
  const [reminderDismissed, setReminderDismissed] = useState(false);

  // New dashboard state
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [timeFilter, setTimeFilter] = useState('30d');
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Get user email from Supabase session instead of localStorage
  useEffect(() => {
    const getSessionEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setTeacherEmail(session.user.email);
      }
    };
    getSessionEmail();
  }, []);

  // Fetch and subscribe to requests
  useEffect(() => {
    if (!teacherEmail) {
      return;
    }
    let mounted = true;
    async function fetchRequests() {
      const { data, error } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .order('created_at', { ascending: false });

      if (mounted) setRequests((data || []).filter(req => req.status === 'pending'));
    }
    fetchRequests();
    const channel = supabase
      .channel('teacher-requests')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'booking_requests', filter: `teacher_email=eq.${teacherEmail}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setRequests(prev => {
              // Only add if not already present and status is pending
              if (payload.new.status !== 'pending') return prev;
              if (prev.some(req => req.id === payload.new.id)) return prev;
              return [payload.new, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setRequests(prev => {
              // Remove if not pending anymore
              if (payload.new.status !== 'pending') return prev.filter(req => req.id !== payload.new.id);
              // Otherwise update
              return prev.map((req) => (req.id === payload.new.id ? payload.new : req));
            });
          } else if (payload.eventType === 'DELETE') {
            setRequests(prev => prev.filter((req) => req.id !== payload.old.id));
          }
        }
      )
      .subscribe();
    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [teacherEmail]);

  // Fetch student profiles for all requests
  useEffect(() => {
    async function fetchProfiles() {
      const emails = Array.from(new Set(requests.map(r => r.student_email)));
      if (emails.length === 0) return;
      const { data } = await supabase.from('students').select('*').in('email', emails);
      const profiles = {};
      (data || []).forEach(s => { profiles[s.email] = s; });
      setStudentProfiles(profiles);
    }
    fetchProfiles();
  }, [requests]);

  // Get user on mount
  useEffect(() => {
    async function getUser() {
      const { data: { user }, error } = await supabase.auth.getUser();
      setUser(user);
    }
    getUser();
  }, []);

  // Fetch conversations when user loads - OPTIMIZED with batch queries
  useEffect(() => {
    if (!user) return;

    async function fetchConversations() {
      try {
        const startTime = Date.now();

        // 1. Fetch conversations where the current user is involved (1 query)
        const { data: conversations, error } = await supabase
          .from('conversations')
          .select('*')
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .order('created_at', { ascending: false });

        if (error) {
          logger.error('Error fetching conversations:', error);
          return;
        }

        setConversations(conversations || []);

        if (conversations && conversations.length > 0) {
          logger.debug(`[Performance] Found ${conversations.length} conversations`);

          // 2. Extract all unique user IDs from conversations (no query, just processing)
          const userIds = new Set();
          conversations.forEach(conv => {
            const otherUserId = user.id === conv.user1_id ? conv.user2_id : conv.user1_id;
            userIds.add(otherUserId);
          });

          const userIdsArray = Array.from(userIds);
          logger.debug(`[Performance] Need to fetch details for ${userIdsArray.length} unique users`);

          // 3. Batch fetch all user details (2 queries total: students + teachers)
          const userDetailsMap = await batchGetUserDetails(userIdsArray);
          setUserDetails(userDetailsMap);

          // 4. Batch fetch last messages for all conversations (1 query)
          const conversationIds = conversations.map(c => c.id);
          const { data: allMessages } = await supabase
            .from('messages')
            .select('conversation_id, content, created_at, sender_id')
            .in('conversation_id', conversationIds)
            .order('created_at', { ascending: false });

          // 5. Build last messages map (take first/latest message per conversation)
          const lastMsgs = {};
          if (allMessages) {
            allMessages.forEach(msg => {
              // Only store if we haven't seen this conversation yet (messages are ordered by created_at DESC)
              if (!lastMsgs[msg.conversation_id]) {
                lastMsgs[msg.conversation_id] = msg;
              }
            });
          }

          setLastMessages(lastMsgs);

          logger.debug(`[Performance] Loaded ${Object.keys(userDetailsMap).length} users and ${Object.keys(lastMsgs).length} last messages`);
        }

        const duration = Date.now() - startTime;
        logger.debug(`[Performance] fetchConversations completed in ${duration}ms`);
      } catch (error) {
        logger.error('Error in fetchConversations:', error);
      }
    }

    fetchConversations();

    // Subscribe to real-time updates for conversations and messages
    // OPTIMIZED: Filter to only listen to this teacher's conversations
    if (user) {
      const conversationsChannel = supabase.channel('dashboard-conversations')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: `user1_id=eq.${user.id}` // Only conversations where teacher is user1
          },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              setConversations(prev => [payload.new, ...prev]);
            }
          }
        )
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: `user2_id=eq.${user.id}` // OR conversations where teacher is user2
          },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              setConversations(prev => [payload.new, ...prev]);
            }
          }
        )
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${user.id}` // Only messages TO this teacher
          },
          async (payload) => {
            if (payload.eventType === 'INSERT') {
              // New message received
              const newMessage = payload.new;

              // Update last messages
              setLastMessages(prev => ({
                ...prev,
                [newMessage.conversation_id]: newMessage
              }));

              // If this is a new user, fetch their details using batch service
              if (!userDetails[newMessage.sender_id]) {
                const detailsMap = await batchGetUserDetails([newMessage.sender_id]);
                const details = detailsMap[newMessage.sender_id];
                if (details) {
                  setUserDetails(prev => ({
                    ...prev,
                    [newMessage.sender_id]: details
                  }));
                }
              }
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(conversationsChannel);
      };
    }
  }, [user]);

  // Fetch dashboard metrics data
  useEffect(() => {
    if (!teacherEmail) return;

    async function fetchDashboardData() {
      setMetricsLoading(true);
      try {
        const data = await fetchAllDashboardData(teacherEmail, timeFilter);
        setDashboardMetrics(data);
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
      } finally {
        setMetricsLoading(false);
      }
    }

    fetchDashboardData();
  }, [teacherEmail, timeFilter]);

  // Accept/Reject handler
  async function handleAction(reqId, status) {
    setActionLoading(reqId + '-' + status);
    setActionError('');
    setActionSuccess('');
    try {
      const { data, error } = await updateBookingRequestStatus(reqId, status);
      if (error) throw error;

      // Refresh the pending requests from database
      const { data: refreshedRequests } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (refreshedRequests) {
        setRequests(refreshedRequests);
      }

      setActionSuccess(status === 'accepted' ? 'Request accepted successfully!' : 'Request rejected successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setActionError('Failed to update request: ' + (err.message || err));
    } finally {
      setActionLoading(null);
    }
  }

  // Calculate real notifications
  const getNotificationCount = () => {
    let count = 0;
    
    // Count pending requests
    count += requests.length;
    
    // Count unread messages (messages from others in the last 24 hours)
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    Object.values(lastMessages).forEach(message => {
      if (message && 
          message.sender_id !== user?.id && 
          new Date(message.created_at) > oneDayAgo) {
        count += 1;
      }
    });
    
    return count;
  };

  const notificationCount = getNotificationCount();

  // Calculate separate counts for sidebar
  const requestCount = requests.length;
  const chatCount = Object.values(lastMessages).filter(message => {
    if (!message || message.sender_id === user?.id) return false;
    const now = new Date();
    const messageTime = new Date(message.created_at);
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return messageTime > oneDayAgo;
  }).length;

  // Platform reminder banner component
  const PlatformReminderBanner = () => {
    if (reminderDismissed) return null;

    return (
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        margin: isMobile ? '16px auto 12px auto' : '0 auto 24px auto',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: isMobile ? 10 : 12,
        padding: isMobile ? '12px 38px 12px 12px' : '16px 40px 16px 16px',
        border: '1px solid #fcd34d',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 12 : 12,
        position: 'relative'
      }}>
        <FaLock size={24} style={{ flexShrink: 0, color: '#92400e' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700,
            fontSize: isMobile ? 14 : 14,
            color: '#92400e',
            marginBottom: isMobile ? 4 : 4,
            lineHeight: 1.3
          }}>
            {isArabic ? 'تذكير: استخدام المنصة' : 'Reminder: Platform Usage'}
          </div>
          <div style={{
            fontSize: isMobile ? 12 : 12,
            color: '#78350f',
            lineHeight: 1.4,
            wordWrap: 'break-word'
          }}>
            {isArabic
              ? 'جميع الحجوزات يجب أن تتم عبر المنصة.'
              : 'All bookings must be made through the platform.'}
          </div>
        </div>
        <button
          onClick={() => setReminderDismissed(true)}
          style={{
            position: 'absolute',
            top: isMobile ? 10 : 12,
            right: isArabic ? 'auto' : (isMobile ? 10 : 12),
            left: isArabic ? (isMobile ? 10 : 12) : 'auto',
            background: 'rgba(146, 64, 14, 0.15)',
            border: 'none',
            color: '#92400e',
            fontSize: isMobile ? 19 : 20,
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
            minWidth: isMobile ? 28 : 24,
            minHeight: isMobile ? 28 : 24,
            width: isMobile ? 28 : 24,
            height: isMobile ? 28 : 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            transition: 'all 0.2s ease',
            flexShrink: 0,
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(146, 64, 14, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(146, 64, 14, 0.15)';
          }}
          aria-label="Dismiss reminder"
        >
          ✕
        </button>
      </div>
    );
  };

  if (isArabic) {
    return (
      <div className="flex min-h-screen w-full bg-[#f6f6f7]" dir="rtl">
        <Sidebar userRole="teacher" requestCount={requestCount} chatCount={chatCount} />
        <main
          className="flex-1 flex flex-col justify-start items-center pb-8 pt-4 md:pt-10 px-2 md:px-10"
          style={{
            paddingTop: isMobile ? '8px' : undefined,
            paddingBottom: isMobile ? '16px' : undefined,
          }}
        >
          {/* Welcome Header */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-6" dir="rtl">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              مرحبًا بعودتك، {user?.user_metadata?.full_name 
                ? transliterateNameSync(user.user_metadata.full_name, true)
                : 'معلم'}
            </h1>
            <p className="text-gray-600 text-sm md:text-base">
              هذا ما يحدث مع طلابك اليوم.
            </p>
          </div>

          {/* Metrics Row */}
          {!metricsLoading && dashboardMetrics && (
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <MetricCard
                  title="إجمالي الأرباح"
                  value={new Intl.NumberFormat('ar-BH', { style: 'currency', currency: 'BHD' }).format(dashboardMetrics.earnings?.total || 0)}
                  change={dashboardMetrics.earnings?.change || 0}
                  changeType={dashboardMetrics.earnings?.change >= 0 ? 'increase' : 'decrease'}
                  icon={<FaDollarSign style={{ color: '#10b981' }} />}
                  isArabic={true}
                />
                <MetricCard
                  title="الطلاب النشطون"
                  value={dashboardMetrics.students?.count || 0}
                  change={dashboardMetrics.students?.change || 0}
                  changeType={dashboardMetrics.students?.change >= 0 ? 'increase' : 'decrease'}
                  icon={<FaUsers style={{ color: '#6366f1' }} />}
                  isArabic={true}
                />
                <MetricCard
                  title="الجلسات القادمة"
                  value={dashboardMetrics.upcomingSessions?.length || 0}
                  subtitle="هذا الأسبوع"
                  icon={<FaCalendarAlt style={{ color: '#3b82f6' }} />}
                  isArabic={true}
                />
                <MetricCard
                  title="مشاهدات الملف"
                  value={dashboardMetrics.profileViews?.count || 0}
                  change={dashboardMetrics.profileViews?.change || 0}
                  changeType={dashboardMetrics.profileViews?.change >= 0 ? 'increase' : 'decrease'}
                  icon={<FaEye style={{ color: '#8b5cf6' }} />}
                  isArabic={true}
                />
              </div>
            </div>
          )}

          {/* Chart and Sessions Row */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Earnings Chart */}
              <div className="lg:col-span-2">
                <EarningsChart
                  data={dashboardMetrics?.earningsHistory || []}
                  timeFilter={timeFilter}
                  onTimeFilterChange={setTimeFilter}
                  isArabic={true}
                />
              </div>

              {/* Upcoming Sessions */}
              <div className="lg:col-span-1">
                <UpcomingSessionsList
                  sessions={dashboardMetrics?.upcomingSessions || []}
                  limit={5}
                  isArabic={true}
                  onViewAll={() => navigate("/dashboard/teacher/calendar")}
                  onOpenCalendar={() => navigate("/dashboard/teacher/calendar")}
                />
              </div>
            </div>
          </div>

          {/* Student Requests Table */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
            <StudentRequestsTable
              requests={requests}
              onAccept={(reqId) => handleAction(reqId, 'accepted')}
              onReject={(reqId) => handleAction(reqId, 'rejected')}
              isArabic={true}
              onViewStudent={(student) => setProfileModal({ open: true, student })}
              studentProfiles={studentProfiles}
              limit={5}
              actionLoading={actionLoading}
            />
          </div>

          {/* Notification Button */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              position: 'fixed',
              top: 18,
              right: 18,
              zIndex: 2100,
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
            aria-label="Show notifications"
          >
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#FF7A00">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {notificationCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                background: '#232B3E',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white'
              }}>
                {toArabicNumerals(notificationCount > 99 ? '99+' : notificationCount, isArabic)}
              </span>
            )}
          </button>
          {/* Teacher Rank Widget */}
          <div className="w-full max-w-6xl mx-auto mt-3 md:mt-8 px-2 md:px-0">
            <TeacherRankWidget isArabic={true} />
          </div>
        </main>
        <StudentProfileModal open={profileModal.open} onClose={() => setProfileModal({ open: false, student: null })} student={profileModal.student} />
        <RequestDetailsModal open={requestModal.open} onClose={() => setRequestModal({ open: false, request: null })} request={requestModal.request} />
        <NotificationDropdown 
          open={showNotifications} 
          onClose={() => setShowNotifications(false)} 
          notifications={notifications}
          requests={requests}
          lastMessages={lastMessages}
          user={user}
          isArabic={isArabic}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-[#f6f6f7]">
      <Sidebar userRole="teacher" requestCount={requestCount} chatCount={chatCount} />
      <main
        className="flex-1 flex flex-col justify-start items-center pb-8 pt-4 md:pt-10 px-2 md:px-10"
        style={{
          paddingTop: isMobile ? '8px' : undefined,
          paddingBottom: isMobile ? '16px' : undefined,
        }}
      >
        {/* Welcome Header */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-6 pt-6 md:pt-0">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {isArabic ? 'مرحبًا بعودتك، ' : 'Welcome back, '}
            {user?.user_metadata?.full_name 
              ? (isArabic ? transliterateNameSync(user.user_metadata.full_name, isArabic) : user.user_metadata.full_name)
              : (isArabic ? 'المعلم' : 'Teacher')}
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            {isArabic ? 'إليك ما يحدث مع طلابك اليوم.' : "Here is what's happening with your students today."}
          </p>
        </div>

        {/* Metrics Row */}
        {!metricsLoading && dashboardMetrics && (
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <MetricCard
                title={isArabic ? 'إجمالي الأرباح' : 'Total Earnings'}
                value={new Intl.NumberFormat('en-BH', { style: 'currency', currency: 'BHD' }).format(dashboardMetrics.earnings?.total || 0)}
                change={dashboardMetrics.earnings?.change || 0}
                changeType={dashboardMetrics.earnings?.change >= 0 ? 'increase' : 'decrease'}
                icon={<FaDollarSign style={{ color: '#10b981' }} />}
                isArabic={isArabic}
              />
              <MetricCard
                title={isArabic ? 'الطلاب النشطون' : 'Active Students'}
                value={dashboardMetrics.students?.count || 0}
                change={dashboardMetrics.students?.change || 0}
                changeType={dashboardMetrics.students?.change >= 0 ? 'increase' : 'decrease'}
                icon={<FaUsers style={{ color: '#6366f1' }} />}
                isArabic={isArabic}
              />
              <MetricCard
                title={isArabic ? 'الجلسات القادمة' : 'Upcoming Sessions'}
                value={dashboardMetrics.upcomingSessions?.length || 0}
                subtitle={isArabic ? 'هذا الأسبوع' : 'This Week'}
                icon={<FaCalendarAlt style={{ color: '#3b82f6' }} />}
                isArabic={isArabic}
              />
              <MetricCard
                title={isArabic ? 'مشاهدات الملف الشخصي' : 'Profile Views'}
                value={dashboardMetrics.profileViews?.count || 0}
                change={dashboardMetrics.profileViews?.change || 0}
                changeType={dashboardMetrics.profileViews?.change >= 0 ? 'increase' : 'decrease'}
                icon={<FaEye style={{ color: '#8b5cf6' }} />}
                isArabic={isArabic}
              />
            </div>
          </div>
        )}

        {/* Chart and Sessions Row */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Earnings Chart */}
            <div className="lg:col-span-2">
              <EarningsChart
                data={dashboardMetrics?.earningsHistory || []}
                timeFilter={timeFilter}
                onTimeFilterChange={setTimeFilter}
                isArabic={false}
              />
            </div>

            {/* Upcoming Sessions */}
            <div className="lg:col-span-1">
              <UpcomingSessionsList
                sessions={dashboardMetrics?.upcomingSessions || []}
                limit={5}
                isArabic={false}
                onViewAll={() => navigate("/dashboard/teacher/calendar")}
                onOpenCalendar={() => navigate("/dashboard/teacher/calendar")}
              />
            </div>
          </div>
        </div>

        {/* Student Requests Table */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-8">
          <StudentRequestsTable
            requests={requests}
            onAccept={(reqId) => handleAction(reqId, 'accepted')}
            onReject={(reqId) => handleAction(reqId, 'rejected')}
            isArabic={false}
            onViewStudent={(student) => setProfileModal({ open: true, student })}
            studentProfiles={studentProfiles}
            limit={5}
            actionLoading={actionLoading}
          />
        </div>

        {/* Notification Button */}
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          style={{
            position: 'fixed',
            top: 18,
            right: 18,
            zIndex: 2100,
            background: 'transparent',
            border: 'none',
            borderRadius: 8,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
          aria-label="Show notifications"
        >
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#FF7A00">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {notificationCount > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              background: '#232B3E',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white'
            }}>
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>
        {/* Teacher Rank Widget */}
        <div className="w-full max-w-6xl mx-auto mt-3 md:mt-8 px-2 md:px-0">
          <TeacherRankWidget isArabic={false} />
        </div>
        <StudentProfileModal open={profileModal.open} onClose={() => setProfileModal({ open: false, student: null })} student={profileModal.student} />
        <RequestDetailsModal open={requestModal.open} onClose={() => setRequestModal({ open: false, request: null })} request={requestModal.request} />
        <NotificationDropdown 
          open={showNotifications} 
          onClose={() => setShowNotifications(false)} 
          notifications={notifications}
          requests={requests}
          lastMessages={lastMessages}
          user={user}
          isArabic={isArabic}
        />
      </main>
    </div>
  );
} 